import { yupResolver } from '@hookform/resolvers/yup';
import { FormControl, IconButton, TextField } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useContext, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useHistory, useParams } from 'react-router';
import * as yup from 'yup';
import LoginButton from 'components/Buttons/LoginButton';
import { notify } from 'components/Notifier';
import { AppContext } from 'context';
import Layout from 'pages/Login/Layout';
import MemoEyeCloseIcon from 'components/Icons/EyeCloseIcon';
import MemoEyeOpenIcon from 'components/Icons/EyeOpenIcon';

const useStyles = makeStyles({
  form: {
    display: 'flex',
    flexFlow: 'column',

    '& > *': {
      marginBottom: '1rem',
    },

    '& > h2': {
      marginTop: 0,
    },
  },
  paper: {
    maxWidth: '520px',
  },
  title: {
    fontSize: '42px',
  },
  eyeButton: {
    width: '50px',
    height: '50px',
    position: 'absolute',
    marginTop: '9px',
    marginLeft: '90%',
  },
  eyeButtonConfirmation: {
    width: '50px',
    height: '50px',
    position: 'absolute',
    marginTop: '9px',
    marginLeft: '90%',
  },
  formControl: {
    width: '100%',
  },
});

const ChangePasswordForm: React.FC = () => {
  const history = useHistory();
  const context = useContext(AppContext);
  const isEdgeBrowser = context?.isEdgeBrowser?.() ?? false;
  const classes = useStyles();
  const { t } = useTranslation();
  let { token } = useParams<{ token: string }>();
  const [showPassword, setShowPassword] = useState(false);
  const [showPasswordConfirmation, setShowPasswordConfirmation] = useState(false);

  const schema = yup.object().shape({
    password: yup
      .string()
      .required(t('loginForm.validation.requiredPassword'))
      .matches(/^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%^&*?])(?=.{8,})/, t('loginForm.validation.passwordRules')),
    password2: yup
      .string()
      .oneOf([yup.ref('password'), null], t('loginForm.validation.confirmationMatch'))
      .required(t('loginForm.validation.requiredConfirmationPassword')),
  });

  const {
    control,
    handleSubmit,
    formState: { isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      password: '',
      password2: '',
    },
  });

  const onSubmit = async (values: any) => {
    try {
      await context.changePassword(token, {
        password: values.password,
      });
      notify({
        message: t('loginForm.resetConfirmation'),
        severity: 'success',
      });
      history.push('/');
    } catch (e) {
      notify({
        message: t('loginForm.errorReset'),
        severity: 'error',
      });
      history.push('/');
    }
  };

  return (
    <Layout
      classes={{ paper: classes.paper, title: classes.title }}
      title={t('loginForm.resetTitle.title')}
      subtitle={t('loginForm.resetTitle.subtitle')}
    >
      <form className={classes.form} onSubmit={handleSubmit(onSubmit)}>
        <FormControl className={classes.formControl}>
          <Controller
            name="password"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <>
                <TextField
                  type={showPassword ? 'text' : 'password'}
                  label={t('loginForm.password')}
                  error={!!error}
                  helperText={error?.message}
                  inputRef={ref}
                  {...inputProps}
                />
                {!isEdgeBrowser && (
                  <IconButton
                    className={classes.eyeButton}
                    aria-label="toggle password visibility"
                    onClick={() => setShowPassword(!showPassword)}
                    onMouseDown={event => event.preventDefault()}
                  >
                    {showPassword ? <MemoEyeCloseIcon /> : <MemoEyeOpenIcon />}
                  </IconButton>
                )}
              </>
            )}
          />
        </FormControl>
        <FormControl className={classes.formControl}>
          <Controller
            name="password2"
            control={control}
            render={({ field: { ref, ...inputProps }, fieldState: { error } }) => (
              <>
                <TextField
                  type={showPasswordConfirmation ? 'text' : 'password'}
                  label={t('loginForm.passwordConfirm')}
                  error={!!error}
                  helperText={error?.message}
                  inputRef={ref}
                  {...inputProps}
                />
                {!isEdgeBrowser && (
                  <IconButton
                    className={classes.eyeButtonConfirmation}
                    aria-label="toggle password visibility"
                    onClick={() => setShowPasswordConfirmation(!showPasswordConfirmation)}
                    onMouseDown={event => event.preventDefault()}
                  >
                    {showPasswordConfirmation ? <MemoEyeCloseIcon /> : <MemoEyeOpenIcon />}
                  </IconButton>
                )}
              </>
            )}
          />
        </FormControl>

        <LoginButton disabled={isSubmitting}>{t('loginForm.resetPassword')}</LoginButton>
      </form>
    </Layout>
  );
};

export default ChangePasswordForm;
